import styles from './EnhancedMedia.module.scss';

import cx from 'classnames';
import type { ReferencedEntry } from 'contentful';
import React, { SyntheticEvent } from 'react';

import type {
  HasAmbient,
  HasExternalSources,
  HasImage,
  HasMedia,
  HasMobileImage,
  HasVideo,
} from 'api/contentful/contentTypes.cloverdotcom';
import { getField } from 'utils/EntryUtil';
import { Background } from '../Contentful/Background';
import type { ContentfulImage } from '../Contentful/ContentfulImage';
import { Video } from '../Contentful/Video';
import { ExternalMediaModalLink } from '../ExternalMediaModalLink/ExternalMediaModalLink';
import { Media, MediaProps } from '../Media/Media';
import { MuteUnmuteButton } from './MuteUnmuteButton/MuteUnmuteButton';
import { PlayPauseButton } from './PlayPauseButton/PlayPauseButton';

export enum DisplayMode {
  AUTO,
  MEDIA_ONLY, // always render video if present in entry
  IMAGE_ONLY, // always render image, even if a video is present
}

type Props = React.AllHTMLAttributes<HTMLElement>;
export interface IEnhancedMediaProps extends Props {
  entry?: ReferencedEntry<HasVideo & HasImage & HasMobileImage & HasExternalSources & HasAmbient & HasMedia>;

  // makes the media height 100%
  preferHeight?: boolean;

  // makes the media width 100%;
  preferWidth?: boolean;

  // always disable the white frame
  disableFrame?: boolean;

  innerRef?: React.Ref<HTMLDivElement>;

  mediaProps?: Pick<MediaProps, Exclude<keyof MediaProps, 'image' | 'video'>>;

  displayMode?: DisplayMode;

  useBackgroundCover?: boolean;

  isYoutubeModal?: boolean;

  isTextMediaModule?: boolean;

  videoPlayerClassName?: string;
}

export interface IEnhancedMediaState {
  paused: boolean;
  muted: boolean;
  overrideEntry?: ReferencedEntry<HasVideo & HasImage>;
}

export class EnhancedMedia extends React.PureComponent<IEnhancedMediaProps, IEnhancedMediaState> {
  get isPaused() {
    return this.state.paused;
  }
  set isPaused(paused: boolean) {
    if (paused === this.isPaused) return;

    this.setState({ paused });
  }

  get isMuted() {
    return this.state.muted;
  }
  set isMuted(muted: boolean) {
    if (muted === this.isMuted) return;

    this.setState({ muted });
  }

  get activeEntry(): ReferencedEntry<HasExternalSources & HasVideo & HasImage & HasMedia> | undefined {
    const { entry } = this.props;
    const { overrideEntry } = this.state;

    return overrideEntry && overrideEntry.fields ? overrideEntry : entry;
  }

  set activeEntry(entry: ReferencedEntry<HasExternalSources & HasVideo & HasImage & HasMedia> | undefined) {
    if (!entry) {
      this.setState({ overrideEntry: undefined });
      return;
    }

    const activeEntry = this.activeEntry;
    if (activeEntry && activeEntry.sys.id === entry.sys.id) return;

    this.setState({ overrideEntry: entry });
  }

  media = React.createRef<ContentfulImage | Video>();

  constructor(props: IEnhancedMediaProps, context?: any) {
    super(props, context);

    const { autoPlay, muted } = props;

    this.state = {
      paused: !(autoPlay && muted),
      muted: !!muted,
    };
  }

  readonly onPlayPauseClick: React.MouseEventHandler<HTMLButtonElement> = e => {
    if (!(this.media.current instanceof Video)) return;

    const video = this.media.current as Video;

    if (this.isPaused) {
      video.play();
    } else {
      video.pause();
    }
    // to prevent modal triggering
    e.stopPropagation();
    e.preventDefault();
  };

  readonly onMuteUnmuteClick: React.MouseEventHandler<HTMLButtonElement> = e => {
    if (!(this.media.current instanceof Video)) return;

    this.media.current.isMuted = !this.isMuted;
    // to prevent modal triggering
    e.stopPropagation();
    e.preventDefault();
  };

  readonly onPause = () => (this.isPaused = true);
  readonly onPlay = () => (this.isPaused = false);

  readonly onVolumeChange = (e: SyntheticEvent<HTMLVideoElement | HTMLImageElement>) => {
    if (!(e.target instanceof HTMLVideoElement)) return;

    this.isMuted = e.target.muted;
  };

  play() {
    const { current } = this.media;
    if (current instanceof Video) {
      current.play();
    }
  }

  pause() {
    const { current } = this.media;
    if (current instanceof Video) {
      current.pause();
    }
  }

  // forces reload on media to revert back to background image
  reloadMedia() {
    const { current } = this.media;
    if (current instanceof Video && current.el) {
      current.el.load();
      this.isPaused = current.el.paused;
    }
  }

  componentDidMount() {
    const { current } = this.media;
    if (current instanceof Video && current.el) {
      this.isPaused = current.el.paused;
    }
  }

  render() {
    const {
      entry,
      displayMode,
      innerRef,
      className,
      preferHeight,
      preferWidth,
      disableFrame,
      autoPlay,
      muted,
      loop,
      controls,
      preload,
      mediaProps,
      useBackgroundCover,
      isYoutubeModal,
      isTextMediaModule,
      videoPlayerClassName,
      // make sure that this only has attributes for the dom element
      ...props
    } = this.props;

    const activeEntry = this.activeEntry;
    if (!activeEntry || !activeEntry.fields) return null;
    const { video, image, youtubeId, media: MediaSource } = activeEntry.fields;

    const prioritizeImage = image && displayMode === DisplayMode.IMAGE_ONLY;
    const noMediaPresent =
      !video && !youtubeId && displayMode !== DisplayMode.MEDIA_ONLY && !(MediaSource && MediaSource.fields);
    const useBackgroundImage = (prioritizeImage || noMediaPresent) && !isTextMediaModule;
    const mergedMediaProps = { autoPlay, muted, loop, preload, controls, ...mediaProps };

    const media = (
      <Media
        ref={this.media}
        className={cx({
          [styles.mediaPreferHeight]: preferHeight,
          [styles.mediaPreferWidth]: preferWidth,
        })}
        autoHeight={preferHeight}
        autoWidth={preferWidth}
        onPause={this.onPause}
        onPlay={this.onPlay}
        onVolumeChange={this.onVolumeChange}
        entry={activeEntry}
        canRenderExternal={!isYoutubeModal}
        videoPlayerClassName={videoPlayerClassName}
        {...mergedMediaProps}
      />
    );

    const renderMedia = () =>
      isYoutubeModal ? (
        <ExternalMediaModalLink topic={entry}>
          {media}
          <div className={styles.youtubePlayButton} />
        </ExternalMediaModalLink>
      ) : (
        media
      );

    return (
      <div
        ref={innerRef}
        className={cx(styles.enhancedMedia, className, {
          [styles.enhancedMediaVideo]: !!video,
          [styles.enhancedMediaFramed]: !disableFrame && !controls && !!video,
          [styles.backgroundImage]: useBackgroundImage,
        })}
        {...props}
      >
        {useBackgroundImage ? (
          <Background
            asset={image}
            autoHeight={preferHeight}
            autoWidth={preferWidth}
            fit="fill"
            className={cx(className, {
              [styles.backgroundImage]: useBackgroundImage,
              [styles.backgroundImageCover]: useBackgroundCover,
            })}
            style={{
              ...props.style,
            }}
          />
        ) : (
          <>
            {renderMedia()}
            {video && !mergedMediaProps.controls && (
              <>
                <PlayPauseButton
                  paused={this.isPaused}
                  onClick={this.onPlayPauseClick}
                  className={cx(styles.control, styles.playButton)}
                  isAmbient={getField(entry, 'isAmbient')}
                />
                {!mergedMediaProps.muted && (
                  <MuteUnmuteButton
                    muted={this.isMuted}
                    onClick={this.onMuteUnmuteClick}
                    className={cx(styles.control, styles.muteButton)}
                  />
                )}
              </>
            )}
          </>
        )}
      </div>
    );
  }
}
